import { memo, useCallback, useState, useEffect, useRef, useMemo } from "react";
import { useParameters } from "../../../hooks/useParameters";
import { TenantJobModal } from "../../organisms/modal/TenantJobModal";
import {
  Center,
  Spinner,
  Stack,
  Th,
  Tr,
  useDisclosure,
} from "@chakra-ui/react";
import { PageDiscription } from "../../atoms/button/PageDiscription";
import { TenantJobRow } from "../../organisms/datarow/TenantJobRow";
import { TableContent } from "../../organisms/layout/TableContent";
import { useLoginUser } from "../../../hooks/useLoginUser";
import { ScheduleSettings } from "../../../hooks/useParameters";
import axios from "axios";
import appconfig from "../../../config.json";
import { ChatDrawer } from "../../molecules/ChatDrawer";

/** テーブルのソート対象フィールド */
type SortField = 'name';
/** ソート順序（昇順/降順） */
type SortOrder = 'asc' | 'desc';

export const BatchOperations = memo(() => {
  const dlJob = useDisclosure();
  const { loginUser, selectTenant, tokenRefresh } = useLoginUser();
  const {
    getTenantJobs,
    tenantJobs,
    loading,
  } = useParameters();
  const [selectedTenantJob, setSelectedTenantJob] = useState<ScheduleSettings|undefined>(undefined);
  // ルール情報キャッシュ: eventsRuleName → { schedule, state }
  const [ruleInfoMap, setRuleInfoMap] = useState<Record<string, { schedule?: string; state?: string }>>({});
  // ソート機能の状態管理
  const [sortField, setSortField] = useState<SortField>('name'); // 現在ソート中のフィールド
  const [sortOrder, setSortOrder] = useState<SortOrder>('asc'); // 昇順(asc)または降順(desc)
  const didLogRef = useRef(false);

  // ログインユーザーの管理者権限をチェック
  const isUserAdmin = loginUser && (loginUser.isAdmin || loginUser.isAccountAdmin);

  useEffect(() => {
    if (didLogRef.current === false) {
      didLogRef.current = true;
      let tenant_id: string = "";
      if (selectTenant) {
        tenant_id = selectTenant;
      } else {
        if (loginUser) tenant_id = loginUser.tenant;
      }
      getTenantJobs(tenant_id);
    }
  }, []);

  useEffect(() => {
    if (selectedTenantJob) {
      dlJob.onOpen();
    }
  }, [selectedTenantJob]);

  // ジョブ一覧取得後にEventBridgeルール情報を一括取得
  useEffect(() => {
    if (tenantJobs.length === 0) return;
    const API_URL: string = appconfig["IOT_MANAGEMENT_API_URL"];
    const ruleNames = Array.from(new Set(
      tenantJobs
        .filter(j => j.eventsRuleName && j.eventsRuleName !== "manual")
        .map(j => j.eventsRuleName)
    ));
    if (ruleNames.length === 0) return;

    new Promise((resolve) => resolve(tokenRefresh())).then((token) => {
      Promise.all(
        ruleNames.map(name =>
          axios
            .get(`${API_URL}/eventbridge/${name}`, {
              headers: { Authorization: `Bearer ${token}` },
            })
            .then(res => ({ name, schedule: res.data?.ScheduleExpression, state: res.data?.State }))
            .catch(() => ({ name, schedule: undefined, state: undefined }))
        )
      ).then(results => {
        const map: Record<string, { schedule?: string; state?: string }> = {};
        results.forEach(r => { map[r.name] = { schedule: r.schedule, state: r.state }; });
        setRuleInfoMap(map);
      });
    });
  }, [tenantJobs]);

  const onClickType = useCallback(
    (parampath: string) => {
      let tenant_id: string = "";
      if (selectTenant) {
        tenant_id = selectTenant;
      } else {
        if (loginUser) tenant_id = loginUser.tenant;
      }
      const targetJob = tenantJobs.find((obj) => obj.parampath === parampath);
      if (targetJob) {
        setSelectedTenantJob(targetJob);
        dlJob.onOpen();
      }
    },
    [tenantJobs]
  );

  /**
   * テーブルヘッダークリック時のソート処理
   * - 同じ列を再クリック: 昇順⇔降順を切り替え
   * - 別の列をクリック: その列で昇順ソート開始
   */
  const handleSort = useCallback((field: SortField) => {
    if (sortField === field) {
      // 同じフィールドの場合はソート順を反転
      setSortOrder((prevOrder) => prevOrder === 'asc' ? 'desc' : 'asc');
    } else {
      // 別のフィールドの場合は昇順でソート開始
      setSortField(field);
      setSortOrder('asc');
    }
  }, [sortField]);

  /**
   * ソート済みのテナントジョブリスト
   * - 日本語対応のlocaleCompareでソート
   * - sortField, sortOrderの変更時に再計算
   */
  const sortedTenantJobs = useMemo(() => {
    const sorted = [...tenantJobs].sort((a, b) => {
      const aValue = a[sortField] || '';
      const bValue = b[sortField] || '';
      
      // 日本語対応のソート
      const comparison = aValue.localeCompare(bValue, 'ja');
      return sortOrder === 'asc' ? comparison : -comparison;
    });
    return sorted;
  }, [tenantJobs, sortField, sortOrder]);

  return (
    <Stack>
      <PageDiscription>
        スケジュール実行を選択して詳細を確認・設定してください。
      </PageDiscription>
      {loading ? (
        <Center h="50vh">
          <Spinner color="teal.500" />
        </Center>
      ) : (
        <>
          <TableContent
            heightOffset="50px"
            headerRows={
              <Tr key="header">
                {/* ソート可能なヘッダー: クリックでソート、ホバーでマークの色が変化 */}
                <Th 
                  width="40%" 
                  cursor="pointer" 
                  onClick={() => handleSort('name')}
                  sx={{
                    '& span': {
                      color: 'gray.400', // ソートマークの通常色
                    },
                    '&:hover span': {
                      color: 'teal.500', // ホバー時の色
                    },
                  }}
                >
                  スケジュール名 <span>{sortField === 'name' && (sortOrder === 'asc' ? '▲' : '▼')}</span>
                </Th>
                <Th width="40%">
                  スケジュール
                </Th>
                <Th width="20%">
                  状態
                </Th>
              </Tr>
            }
            bodyRows={sortedTenantJobs.map((obj) => (
              <TenantJobRow
                key={obj.parampath}
                name={obj.name}
                eventsRuleName={obj.eventsRuleName}
                schedule={ruleInfoMap[obj.eventsRuleName]?.schedule || obj.schedule}
                state={ruleInfoMap[obj.eventsRuleName]?.state}
                parampath={obj.parampath}
                onClick={onClickType}
              />
            ))}
          ></TableContent>
        </>
      )}
      <TenantJobModal
        isOpen={dlJob.isOpen}
        onClose={dlJob.onClose}
        TenantJob={selectedTenantJob}
        isEditable={isUserAdmin || undefined}
        tenant_id={selectTenant || (loginUser ? loginUser.tenant : "")}
      />
      <ChatDrawer />
    </Stack>
  );
});
